const FIELD_SELECTOR = [
  "input",
  "select",
  "textarea",
  "[contenteditable='true']",
  "[tabindex]:not([tabindex='-1'])",
].join(",");

const ACTION_SELECTOR = [
  "button",
  "[role='button']",
].join(",");

const SKIP_TYPES = new Set(["hidden", "file", "radio", "checkbox"]);
const FLOW_DISABLED_SELECTOR = "[data-enter-flow='off'], [data-enter-flow='false']";
const INTERNAL_CONTROL_SELECTOR = [
  ".MuiAutocomplete-clearIndicator",
  ".MuiAutocomplete-popupIndicator",
  ".MuiPickersCalendarHeader-iconButton",
  ".MuiIconButton-root",
].join(",");
const REACT_SELECT_INPUT_PREFIX = "react-select-";

const isReactSelectInput = (element) =>
  !!element && String(element.id || "").startsWith(REACT_SELECT_INPUT_PREFIX);

const getCompositeAnchor = (element) => {
  if (!element || !element.closest) return element;

  const muiAutocomplete = element.closest(".MuiAutocomplete-root");
  if (muiAutocomplete) return muiAutocomplete;

  if (isReactSelectInput(element)) {
    return (
      element.closest(".react-select__control") ||
      element.closest("[class*='-control']") ||
      element
    );
  }

  return element;
};

const isVisible = (element) => {
  if (!element) return false;
  if (element.getAttribute("aria-hidden") === "true") return false;
  const style = window.getComputedStyle(element);
  if (style.visibility === "hidden" || style.display === "none") return false;
  const rect = element.getBoundingClientRect();
  return rect.width > 0 && rect.height > 0;
};

const isFocusable = (element) => {
  if (!element || element.disabled) return false;
  if (element.closest(FLOW_DISABLED_SELECTOR)) return false;
  if (element.closest(INTERNAL_CONTROL_SELECTOR)) return false;
  if (element.getAttribute("aria-disabled") === "true") return false;
  if (element.getAttribute("tabindex") === "-1") return false;
  if (SKIP_TYPES.has((element.type || "").toLowerCase())) return false;
  return isVisible(element);
};

const isRealField = (element) => {
  if (!element) return false;
  if (element.matches("input, select, textarea")) return true;
  if (element.getAttribute("contenteditable") === "true") return true;
  const role = element.getAttribute("role");
  return role === "textbox" || role === "combobox" || role === "spinbutton";
};

const hasOpenListbox = (target) => {
  if (!target || !target.closest) return false;

  const reactSelectRoot = target.closest(".react-select__control, [class*='-control']");
  const reactSelectContainer =
    reactSelectRoot?.closest(".react-select__container, [class*='-container']") ||
    target.closest(".react-select__container, [class*='-container']");
  if (
    (reactSelectContainer || isReactSelectInput(target)) &&
    Array.from(document.querySelectorAll("[role='listbox']")).some((listbox) => {
      const rect = listbox.getBoundingClientRect();
      return rect.width > 0 && rect.height > 0;
    })
  ) {
    return true;
  }

  const expandedTarget =
    target.getAttribute("aria-expanded") === "true"
      ? target
      : target.closest('[aria-expanded="true"]');

  if (expandedTarget) {
    const role = expandedTarget.getAttribute("role");
    const popup = expandedTarget.getAttribute("aria-haspopup");

    if (
      role === "combobox" ||
      popup === "listbox" ||
      target.closest(".MuiAutocomplete-root")
    ) {
      return true;
    }
  }

  const controlledListboxId =
    target.getAttribute("aria-controls") || target.getAttribute("aria-owns");
  if (!controlledListboxId) return false;

  const controlledListbox = document.getElementById(controlledListboxId);
  return (
    !!controlledListbox &&
    controlledListbox.getAttribute("role") === "listbox" &&
    isVisible(controlledListbox)
  );
};

const getFlowScope = (element) =>
  element.closest("[data-enter-flow-scope]") ||
  element.closest("form") ||
  element.closest(".MuiDialog-root") ||
  element.closest("main") ||
  document.body;

const unique = (items) =>
  items.filter((element, index, fields) => fields.indexOf(element) === index);

const sortByVisualOrder = (items) =>
  [...items].sort((a, b) => {
    const aOrder = Number(a.getAttribute("data-enter-order"));
    const bOrder = Number(b.getAttribute("data-enter-order"));
    const aHasOrder = Number.isFinite(aOrder);
    const bHasOrder = Number.isFinite(bOrder);
    if (aHasOrder && bHasOrder && aOrder !== bOrder) return aOrder - bOrder;
    if (aHasOrder !== bHasOrder) return aHasOrder ? -1 : 1;

    const aRect = getCompositeAnchor(a).getBoundingClientRect();
    const bRect = getCompositeAnchor(b).getBoundingClientRect();
    const rowDiff = aRect.top - bRect.top;
    if (Math.abs(rowDiff) > 8) return rowDiff;
    return aRect.left - bRect.left;
  });

const getFocusableFields = (scope) => {
  const candidates = unique(Array.from(scope.querySelectorAll(FIELD_SELECTOR)).filter(isFocusable));
  const fields = candidates.filter(isRealField);
  const actions = unique(Array.from(scope.querySelectorAll(ACTION_SELECTOR)).filter(isFocusable));

  return [...sortByVisualOrder(fields), ...sortByVisualOrder(actions)];
};

const getNextField = (target) => {
  const scope = getFlowScope(target);
  const fields = getFocusableFields(scope);
  const currentIndex = fields.indexOf(target);
  if (currentIndex === -1) {
    const targetRect = getCompositeAnchor(target).getBoundingClientRect();
    return fields.find((field) => {
      const fieldRect = getCompositeAnchor(field).getBoundingClientRect();
      if (fieldRect.top < targetRect.top - 8) return false;
      if (Math.abs(fieldRect.top - targetRect.top) <= 8 && fieldRect.left <= targetRect.left + 4) return false;
      return true;
    }) || null;
  }
  return fields[currentIndex + 1] || null;
};

const focusElement = (element) => {
  if (!element) return;

  element.focus();
  if (
    typeof element.select === "function" &&
    element.tagName === "INPUT" &&
    !element.readOnly
  ) {
    element.select();
  }
};

const focusNextField = (target) => {
  focusElement(getNextField(target));
};

const hasNextField = (target) => !!getNextField(target);

const scheduleDropdownSelectionFlow = (target) => {
  window.setTimeout(() => {
    if (!target || !target.isConnected) return;
    focusNextField(target);
  }, 120);
};

const getFallbackFirstField = () => {
  const scope = document.querySelector("main") || document.body;
  return getFocusableFields(scope)[0] || null;
};

const focusFirstFieldOnRouteChange = () => {
  window.setTimeout(() => {
    const active = document.activeElement;
    if (active && active !== document.body && active !== document.documentElement) return;
    focusElement(getFallbackFirstField());
  }, 80);
};

const shouldHandleEnter = (event) => {
  if (event.defaultPrevented) return false;
  if (event.key !== "Enter") return false;
  if (event.shiftKey || event.ctrlKey || event.altKey || event.metaKey) return false;

  const target = event.target;
  if (!target || !target.closest) return false;
  if (target.closest(FLOW_DISABLED_SELECTOR)) return false;
  if (isReactSelectInput(target)) {
    return false;
  }
  const targetRole = target.getAttribute("role");
  const targetIsField =
    target.matches("input, select, textarea") ||
    target.getAttribute("contenteditable") === "true" ||
    targetRole === "textbox" ||
    targetRole === "combobox" ||
    targetRole === "spinbutton" ||
    (targetRole === "button" && target.getAttribute("aria-haspopup") === "listbox");

  if (!targetIsField) return false;
  if (target.tagName === "TEXTAREA") return target.hasAttribute("data-enter-flow");
  if (target.closest(".swal-modal, .swal-overlay")) return false;
  if (hasOpenListbox(target)) return false;
  if (!hasNextField(target)) return false;

  return isFocusable(target);
};

export const enableKeyboardEnterFlow = () => {
  const handleKeyDown = (event) => {
    if (
      event.key === "Enter" &&
      !event.shiftKey &&
      !event.ctrlKey &&
      !event.altKey &&
      !event.metaKey &&
      hasOpenListbox(event.target)
    ) {
      scheduleDropdownSelectionFlow(event.target);
      return;
    }

    if (!shouldHandleEnter(event)) return;

    const target = event.target;

    event.preventDefault();
    event.stopPropagation();

    setTimeout(() => {
      focusNextField(target);
    }, 0);
  };

  document.addEventListener("keydown", handleKeyDown, true);
  window.addEventListener("popstate", focusFirstFieldOnRouteChange);

  const originalPushState = window.history.pushState;
  const originalReplaceState = window.history.replaceState;
  window.history.pushState = function pushStateWithEnterFlow(...args) {
    const result = originalPushState.apply(this, args);
    focusFirstFieldOnRouteChange();
    return result;
  };
  window.history.replaceState = function replaceStateWithEnterFlow(...args) {
    const result = originalReplaceState.apply(this, args);
    focusFirstFieldOnRouteChange();
    return result;
  };

  focusFirstFieldOnRouteChange();

  return () => {
    document.removeEventListener("keydown", handleKeyDown, true);
    window.removeEventListener("popstate", focusFirstFieldOnRouteChange);
    window.history.pushState = originalPushState;
    window.history.replaceState = originalReplaceState;
  };
};
